import { useEffect, useRef, useState } from "react"
import CwForm from "@front-apps/core/components/web/cw-form"
import {
    PageConfig,
    CwComponentConfig,
} from "@front-apps/core/models/pageConfig"
import GroupService from "@front-apps/core/services/group"
import StoreService from "@front-apps/core/services/store"
import NavigationService from "@front-apps/core/services/navigation"
import WebNavigationParamService from "@front-apps/core/services/webNavigationParam"
import ComponentsUtils from "@front-apps/core/utils/components"

interface FormField {
    _id: string
    show: boolean
    required: boolean
}
const CwMainCustomerFormWrapper = (
    props: PageConfig<CwComponentConfig.Form> & {
        onEditChange?: (value: boolean) => void
        onSubmit?: () => void
    }
) => {
    const [formFields, setFormFields] = useState<FormField[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const schemaRef = useRef({})

    const webNavigationParam = WebNavigationParamService.getInstance()
    const { params } = webNavigationParam.get()
    let groupId = StoreService.getInstance().local.get("groupId")
    let pdv = StoreService.getInstance().local.get("pdv")
    let partnerValue = params?.partner

    if (!groupId) {
        groupId = params?.groupId
    }
    if (!pdv) {
        pdv = params?.pdv || 0
    }

    useEffect(() => {
        fetchFormFields()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchFormFields = async () => {
        try {
            const groupCheckInSetting =
                await GroupService.getInstance().getSetting("checkIn", groupId)

            const formFields =
                groupCheckInSetting?.pdvs[pdv]?.customerForm || []

            setFormFields(formFields)
            getValidationSchema(formFields)
            setIsLoading(false)
        } catch (error) {
            console.error("Error fetching form fields:", error)
            NavigationService.getInstance().navigateTo("error", {
                groupId,
            })
        }
    }

    const renderFormFields = () => {
        const fields = formFields
            ?.filter((field) => field.show)
            ?.map((field) => {
                // Add underscore before uppercase letters, then convert to lowercase
                const labelKey = field._id
                    .replace(/([A-Z])/g, "_$1")
                    .toLowerCase()
                const label = `{{#lang.${labelKey}}}`
                if (field._id === "partner") {
                    return {
                        name: field._id,
                        type: "select",
                        config: {
                            fieldType: "text",
                            fieldName: field._id,
                            variant: "outlined",
                            margin: "normal",
                            edit: true,
                            label: label,
                            displayValueMapping: "displayValue",
                            valueMapping: "value",
                            className:
                                "typography  fullWidth customerFormField",
                            autoDefaultSelect: Boolean(partnerValue),
                            displayNull: !field.required,
                            displayNullLabel: !field.required ? "" : undefined,
                            displayNullValue: !field.required ? "" : undefined,
                        },
                        actions: [
                            {
                                actionType: "callback",
                                actionParams: {
                                    functionKey:
                                        "PartnerService.fetchGroupPartners",
                                    params: [groupId, partnerValue, pdv],
                                },
                            },
                        ],
                    }
                }
                if (field._id === "phone") {
                    return {
                        name: field._id,
                        type: "textField",
                        config: {
                            fieldType: "phone",
                            fieldName: field._id,
                            label: label,
                            variant: "outlined",
                            margin: "normal",
                            className:
                                "typography  halfWidthFormField customerFormField",
                            required: field.required,
                        },
                    }
                }
                return {
                    name: field._id,
                    type: "textField",
                    config: {
                        fieldType: "text",
                        fieldName: field._id,
                        label: label,
                        variant: "outlined",
                        margin: "normal",
                        className:
                            "typography  halfWidthFormField customerFormField",
                        required: field.required,
                    },
                }
            })
        return fields || []
    }

    const getValidationSchema = (fields: FormField[]) => {
        const schema: Record<string, any> = {}

        fields
            ?.filter((field) => field.show)
            .forEach((field) => {
                if (field.required) {
                    schema[field._id] = {
                        required: "required",
                    }
                }

                // Add specific validation for certain field types
                if (field._id === "phone") {
                    schema[field._id] = {
                        ...schema[field._id],
                        matches: "^[+|00]{0,1}[0-9]{7,32}$",
                    }
                }

                if (field._id === "email") {
                    schema[field._id] = {
                        ...schema[field._id],
                        email: "MSG_Email_Must_Be_Valid",
                    }
                }
            })

        schemaRef.current = schema
    }

    return !isLoading ? (
        <CwForm
            key="customerWrapperForm"
            {...props}
            fields={renderFormFields()}
            config={{
                ...props.config,
                validationSchema: schemaRef.current,
            }}
        />
    ) : (
        ComponentsUtils.renderLoader()
    )
}

export default CwMainCustomerFormWrapper
